import React, { useState, useEffect } from 'react';
import Sidebar from './components/Sidebar';
import PromptForm from './components/PromptForm';
import BrowserView from './components/BrowserView';
import BrowserOnly from './components/BrowserOnly';
import BrowserWithScriptEditor from './components/BrowserWithScriptEditor';
import Tutorials from './components/Tutorials';
import ScriptEditor from './components/ScriptEditor';
import AIChatStandalone from './components/AIChatStandalone';
import TextToSpeech from './components/TextToSpeech';
import FBSearchTrend from './components/FBSearchTrend';
import Humanizer from './components/Humanizer';
import WorkPlan from './components/WorkPlan';
import Settings from './components/Settings';
import Home from './components/Home';
import LicenseEntry from './components/LicenseEntry';
import LicenseInfo from './components/LicenseInfo';
import Help from './components/Help';
import TrialLicenseModal from './components/TrialLicenseModal';
import OverdueTasksModal from './components/OverdueTasksModal';
import { LicenseProvider, useLicense } from './contexts/LicenseContext';
import { getAvailableModels, isCacheValid } from './services/openRouterService';
import './App.css';

// Main App Component with License Integration
function AppContent() {
  const [url, setUrl] = useState('https://chatgpt.com/');
  const [activeMenu, setActiveMenu] = useState('Home');
  const [selectedFormType, setSelectedFormType] = useState(null);
  const [showOverdueModal, setShowOverdueModal] = useState(false);
  const [overdueTasks, setOverdueTasks] = useState([]);
  
  const { 
    isLicenseValid, 
    isLoading, 
    showTrialModal, 
    hideTrialModal, 
    activateTrial,
    refreshLicenseStatus,
    checkFeatureAccess,
    incrementUsage,
    isTrialUser,
    getTrialInfo
  } = useLicense();

  // Debug logging
  console.log('App render - isLicenseValid:', isLicenseValid, 'showTrialModal:', showTrialModal);

  // Handle trial activation callback (trial is already activated by context, this is just for logging)
  const handleTrialActivated = async (trialLicense) => {
    console.log('✅ Trial activation callback received:', trialLicense);
    // TrialLicenseModal already called activateTrial from context, which handles state updates
    // This callback is just for confirmation/logging
    if (trialLicense && trialLicense.id) {
      console.log('✅ Trial successfully activated:', {
        trialId: trialLicense.id,
        systemId: trialLicense.systemId,
        expiryDate: trialLicense.expiryDate
      });
    }
  };

  // Persist localStorage to disk periodically and on unload (Electron only) so settings and work plans survive relaunch
  useEffect(() => {
    if (typeof window === 'undefined' || !window.electronAPI?.setPersistedStorage) return;
    const save = () => {
      try {
        const snapshot = {};
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k) snapshot[k] = localStorage.getItem(k);
        }
        window.electronAPI.setPersistedStorage(snapshot);
      } catch (e) {}
    };
    const id = setInterval(save, 30000);
    const onBeforeUnload = () => { save(); };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      clearInterval(id);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, []);

  // Automatically refresh models on app startup if cache is expired
  useEffect(() => {
    const refreshModelsOnStartup = async () => {
      // Only refresh if cache is expired or missing
      if (!isCacheValid()) {
        const apiKey = localStorage.getItem('openrouter_api_key');
        if (apiKey && apiKey.trim() !== '') {
          try {
            // Refresh models in background (don't block UI)
            const models = await getAvailableModels(false, apiKey);
            console.log(`Models refreshed on startup: ${models.length} models loaded`);
          } catch (error) {
            console.warn('Failed to refresh models on startup:', error);
            // Silent fail - use hardcoded models as fallback
          }
        }
      }
    };
    
    // Run after a short delay to not block initial render
    const timer = setTimeout(refreshModelsOnStartup, 1000);
    return () => clearTimeout(timer);
  }, []); // Run once on mount

  // Check for overdue tasks on app launch (once per session)
  useEffect(() => {
    if (!isLicenseValid || isLoading || showTrialModal) return;

    // Check if modal was already shown this session
    const modalShown = sessionStorage.getItem('overdueTasksModalShown');
    if (modalShown === 'true') return;

    // Wait a bit for data to load, then check for overdue tasks
    const checkOverdueTasks = () => {
      try {
        const plansData = localStorage.getItem('workPlans');
        if (!plansData) return;

        const plans = JSON.parse(plansData);
        const today = new Date().toISOString().split('T')[0];
        let allOverdueTasks = [];

        plans.forEach(plan => {
          const planTasks = JSON.parse(localStorage.getItem(`workPlanTasks_${plan.id}`) || '[]');
          const overdueTasks = planTasks.filter(task => 
            task.status !== 'completed' && 
            task.dueDate && 
            task.dueDate < today
          ).map(task => ({
            ...task,
            planId: plan.id,
            planName: plan.name,
            planCategory: plan.category
          }));
          
          allOverdueTasks = [...allOverdueTasks, ...overdueTasks];
        });

        if (allOverdueTasks.length > 0) {
          setOverdueTasks(allOverdueTasks);
          setShowOverdueModal(true);
          sessionStorage.setItem('overdueTasksModalShown', 'true');
        }
      } catch (error) {
        console.error('Error checking overdue tasks:', error);
      }
    };

    // Delay check to ensure data is loaded
    const timer = setTimeout(checkOverdueTasks, 1500);
    return () => clearTimeout(timer);
  }, [isLicenseValid, isLoading, showTrialModal]);

  // Hide Electron BrowserView whenever we're not on the Browser menu so it doesn't overlap other screens (e.g. Fiddy AI Chat)
  useEffect(() => {
    if (activeMenu !== 'Browser' && window.electronAPI?.hideBrowserView) {
      window.electronAPI.hideBrowserView();
    }
  }, [activeMenu]);

  // Show loading screen while initializing
  if (isLoading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>Initializing Fiddyscript AI Content Assistant...</p>
      </div>
    );
  }

  const handleFormTypeSelect = (formType) => {
    setSelectedFormType(formType);
    setActiveMenu('Scripts');
  };

  const handleMenuSelect = (menuName) => {
    setActiveMenu(menuName);
  };

  const handleViewOverdueTasks = () => {
    setShowOverdueModal(false);
    setActiveMenu('Work Plan');
    // Store filter in sessionStorage for WorkPlan component to pick up
    sessionStorage.setItem('workPlanTaskFilter', 'overdue');
  };

  const handleCloseOverdueModal = () => {
    setShowOverdueModal(false);
  };

  const handleLicenseValidated = (license) => {
    console.log('License validated successfully:', license);
    // License validation is now handled by LicenseContext
  };

  let filteredFormTypes = undefined;
  if (activeMenu === 'Scripts' && selectedFormType) {
    filteredFormTypes = [selectedFormType];
  } else if (activeMenu === 'Advertisements') {
    filteredFormTypes = 'advertisement';
  } else if (activeMenu === 'Articles & Blogs') {
    filteredFormTypes = 'articles-blogs';
  } else if (activeMenu === 'Customer Service') {
    filteredFormTypes = 'customer-service';
  } else if (activeMenu === 'Ebooks') {
    filteredFormTypes = 'ebook';
  } else if (activeMenu === 'Ecommerce') {
    filteredFormTypes = 'ecommerce';
  } else if (activeMenu === 'Emails') {
    filteredFormTypes = 'emails';
  } else if (activeMenu === 'Letters') {
    filteredFormTypes = 'letter';
  } else if (activeMenu === 'Marketing') {
    filteredFormTypes = 'marketing';
  } else if (activeMenu === 'Podcasts') {
    filteredFormTypes = 'podcast';
  } else if (activeMenu === 'Press Releases') {
    filteredFormTypes = 'press-release';
  } else if (activeMenu === 'Research') {
    filteredFormTypes = 'research';
  } else if (activeMenu === 'Reviews') {
    filteredFormTypes = 'reviews';
  } else if (activeMenu === 'Rewriter') {
    filteredFormTypes = 'rewriter';
  } else if (activeMenu === 'SEO') {
    filteredFormTypes = 'seo';
  } else if (activeMenu === 'Social Media') {
    filteredFormTypes = 'social-media';
  } else if (activeMenu === 'Video Scripts') {
    filteredFormTypes = 'video-scripts';
  } else if (activeMenu === 'Website Copy') {
    filteredFormTypes = 'website-copy';
  } else if (activeMenu === 'Other') {
    filteredFormTypes = 'other';
  }

  const renderContent = () => {
    if (activeMenu === 'Home') {
      return (
        <div className="app-home-col">
          <Home onFormTypeSelect={handleFormTypeSelect} />
        </div>
      );
    }
    if (activeMenu === 'Fiddy AI Chat') {
      // Render standalone AI Chat screen (not a modal)
      return <AIChatStandalone />;
    }
    if (activeMenu === 'Browser') {
      return <BrowserOnly />;
    }
    if (activeMenu === 'Content Editor') {
      return <ScriptEditor />;
    }
    if (activeMenu === 'Text to Speech') {
      return <TextToSpeech />;
    }
    if (activeMenu === 'FB Search Trend') {
      return <FBSearchTrend />;
    }
    if (activeMenu === 'Humanizer') {
      return <Humanizer />;
    }
    if (activeMenu === 'Work Plan') {
      return <WorkPlan />;
    }
    if (activeMenu === 'Tutorials') {
      return <Tutorials />;
    }
    if (activeMenu === 'Settings') {
      return <Settings />;
    }
    if (activeMenu === 'License Info') {
      return <LicenseInfo />;
    }
    if (activeMenu === 'Help') {
      return <Help />;
    }
    return (
      <>
        <div className="app-form-col">
          <PromptForm formTypeFilter={filteredFormTypes} />
        </div>
        <div className="app-browser-col">
          <BrowserWithScriptEditor url={url} setUrl={setUrl} />
        </div>
      </>
    );
  };

  // Show license entry if license is not valid and no trial modal showing
  // Note: Trial modal should show first, but if it doesn't, show license entry as fallback
  if (!isLicenseValid && !showTrialModal && !isLoading) {
    return (
      <LicenseEntry onLicenseValidated={handleLicenseValidated} />
    );
  }

  return (
    <div className="app-root-row-3col">
      <div className="app-sidebar-col">
        <Sidebar activeMenu={activeMenu} onMenuSelect={handleMenuSelect} />
      </div>
      {renderContent()}
      
      {/* Trial License Modal */}
      <TrialLicenseModal
        isOpen={showTrialModal}
        onClose={hideTrialModal}
        onTrialActivated={handleTrialActivated}
      />
      
      {/* Overdue Tasks Modal */}
      <OverdueTasksModal
        isOpen={showOverdueModal}
        onClose={handleCloseOverdueModal}
        overdueTasks={overdueTasks}
        onViewTasks={handleViewOverdueTasks}
      />
    </div>
  );
}

// Main App Component with License Provider
function App() {
  const [storageRestored, setStorageRestored] = useState(false);

  // Restore persisted storage from disk on launch (Electron only) so API settings and work plans survive relaunch
  useEffect(() => {
    if (typeof window === 'undefined' || !window.electronAPI?.getPersistedStorage) {
      setStorageRestored(true);
      return;
    }
    window.electronAPI.getPersistedStorage().then((data) => {
      if (data && typeof data === 'object') {
        for (const key of Object.keys(data)) {
          try {
            if (data[key] != null) localStorage.setItem(key, String(data[key]));
          } catch (e) {}
        }
      }
      setStorageRestored(true);
    }).catch(() => setStorageRestored(true));
  }, []);

  if (!storageRestored) {
    return (
      <div className="app-loading" style={{ padding: 20, textAlign: 'center', fontFamily: 'sans-serif' }}>
        Loading...
      </div>
    );
  }

  return (
    <LicenseProvider>
      <AppContent />
    </LicenseProvider>
  );
}

export default App;